import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MANUAL_SECTION_LABELS, type YggManualSectionKey } from '@se-qa/domain';
import { api, downloadBlob } from '../lib/api';
import { dayRangeError, istanbulDayRange, istanbulDisplayDate, istanbulToday } from '../lib/istanbul-date';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../lib/auth';

/**
 * AS9100D §9.3 — Yönetimin Gözden Geçirmesi (YGG) sayfası (SE-PR-06).
 *
 * Görünümler: liste (varsayılan) ↔ detay (seçili kayıt). autoInputs salt-okunur
 * snapshot'tır (kayıt oluşturulurken hesaplanır); previousActions ve manuel
 * bölümler/kararlar toplantıda düzenlenir, tek PATCH ile kaydedilir.
 */

const MANUAL_SECTION_KEYS = Object.keys(MANUAL_SECTION_LABELS) as YggManualSectionKey[];

interface ManualSection {
  text: string;
  evaluated: boolean;
}
type ManualInputs = Record<YggManualSectionKey, ManualSection>;

interface YggAction {
  id: string;
  description: string;
  owner: string;
  dueDate: string | null;
  status: 'OPEN' | 'DONE';
  /** Makine-okunur bağ (CAPA `_id`) — raporlara basılmaz. */
  capaId: string | null;
  /** Denetçinin çözebildiği referans (CAPA-2026-0001) — PDF bunu basar. */
  capaNumber: string | null;
}

/** Aksiyonun CAPA bağı — `_id` ve insan-okunur numara birlikte set edilir. */
type CapaRef = { capaId: string | null; capaNumber: string | null };

interface YggDecision {
  id: string;
  text: string;
  actions: YggAction[];
}

interface YggPrevAction {
  yggNumber: string;
  description: string;
  owner: string;
  dueDate: string | null;
  status: 'OPEN' | 'DONE';
  capaId: string | null;
  capaNumber: string | null;
}

interface CountBucket {
  key: string;
  label: string;
  count: number;
}

interface YggAutoInputs {
  kpi: {
    goodPartsPercent: number | null;
    onTimeDeliveryPercent: number | null;
    scrapRatePercent: number | null;
    customerSatisfactionPercent: number | null;
    shipmentLines: { scored: number };
  };
  ncr: {
    totalNcrs: number;
    totalScrapQty: number;
    totalReworkQty: number;
    topPareto: CountBucket[];
    bySource: CountBucket[];
  };
  capa: {
    open: number;
    overdue: number;
    closedInPeriod: number;
    criticalOpen: { capaNumber: string; title: string; severity: string; dueDate: string | null }[];
  };
  audit: {
    findingsByCategory: { MAJOR: number; MINOR: number; OBSERVATION: number; OFI: number };
    runsInPeriod: number;
    lastRunStats: {
      total: number;
      compliant: number;
      weak: number;
      nc: number;
      insufficient: number;
    } | null;
  };
}

type YggStatus = 'DRAFT' | 'FINALIZED';

interface YggRecord {
  _id: string;
  yggNumber: string;
  period: { from: string; to: string };
  meetingDate: string | null;
  participants: string[];
  status: YggStatus;
  autoInputs: YggAutoInputs;
  previousActions: YggPrevAction[];
  manualInputs: ManualInputs;
  decisions: YggDecision[];
  createdAt: string;
  updatedAt: string;
  finalizedAt: string | null;
  finalizedBy: string | null;
}

interface GuideResponse {
  section: YggManualSectionKey;
  label: string;
  questions: string[];
  ragAvailable: boolean;
  passages: { docCode: string; excerpt: string; score: number }[];
}

const STATUS_BADGE: Record<YggStatus, { label: string; class: string }> = {
  DRAFT: {
    label: 'Taslak',
    class: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  },
  FINALIZED: {
    label: 'Kesinleşti',
    class: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  },
};

function newLocalId(): string {
  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function YggPage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('ygg.write');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (selectedId) {
    return <YggDetail id={selectedId} canWrite={canWrite} onBack={() => setSelectedId(null)} />;
  }
  return <YggListView canWrite={canWrite} onSelect={setSelectedId} />;
}

function YggListView({
  canWrite,
  onSelect,
}: {
  canWrite: boolean;
  onSelect: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const list = useQuery<{ items: YggRecord[]; total: number }>({
    queryKey: ['ygg', 'list'],
    queryFn: () => api.get('/v1/ygg'),
  });

  const create = useMutation({
    mutationFn: (payload: unknown) => api.post<YggRecord>('/v1/ygg', payload),
    onSuccess: (rec) => {
      void qc.invalidateQueries({ queryKey: ['ygg', 'list'] });
      setShowForm(false);
      onSelect(rec._id);
    },
  });

  return (
    <div>
      <PageHeader
        title="Yönetimin Gözden Geçirmesi (YGG)"
        subtitle="SE-PR-06 — Dönemsel KPI/NCR/CAPA/Tetkik girdileriyle yönetim gözden geçirme paketi"
        clause="§9.3"
        actions={
          canWrite ? (
            <button type="button" className="btn-primary" onClick={() => setShowForm((v) => !v)}>
              {showForm ? 'Kapat' : 'Yeni YGG'}
            </button>
          ) : undefined
        }
      />

      {showForm && canWrite && (
        <div className="card mb-4 p-5">
          <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
            Yeni YGG Paketi
          </h2>
          <YggCreateForm
            busy={create.isPending}
            error={create.error instanceof Error ? create.error.message : null}
            onSubmit={(payload) => create.mutate(payload)}
          />
        </div>
      )}

      {list.isLoading && (
        <div className="card p-6 text-sm text-slate-500 dark:text-slate-400">Yükleniyor…</div>
      )}
      {list.isError && (
        <div className="card p-6 text-sm text-rose-700 dark:text-rose-300">
          {list.error instanceof Error ? list.error.message : 'Hata'}
        </div>
      )}

      {list.data && (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left">YGG No</th>
                <th className="px-4 py-3 text-left">Dönem</th>
                <th className="px-4 py-3 text-left">Toplantı</th>
                <th className="px-4 py-3 text-left">Durum</th>
                <th className="px-4 py-3 text-left">Aksiyon sayısı</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.items.map((y) => (
                <tr
                  key={y._id}
                  className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  onClick={() => onSelect(y._id)}
                >
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-slate-700 dark:text-slate-300">
                    {y.yggNumber}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-700 dark:text-slate-300">
                    {istanbulDisplayDate(y.period.from)} –{' '}
                    {istanbulDisplayDate(y.period.to)}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                    {y.meetingDate ? new Date(y.meetingDate).toLocaleDateString('tr-TR') : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`badge ${STATUS_BADGE[y.status].class}`}>
                      {STATUS_BADGE[y.status].label}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                    {y.decisions.reduce((s, d) => s + d.actions.length, 0)}
                  </td>
                </tr>
              ))}
              {list.data.items.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                  >
                    Henüz YGG kaydı yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
            {list.data.items.length} kayıt
          </div>
        </div>
      )}
    </div>
  );
}

function YggCreateForm({
  onSubmit,
  busy,
  error,
}: {
  onSubmit: (payload: unknown) => void;
  busy: boolean;
  error: string | null;
}) {
  const [from, setFrom] = useState(istanbulToday(-90));
  const [to, setTo] = useState(istanbulToday(0));
  const [participants, setParticipants] = useState('');
  const rangeError = dayRangeError(from, to);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      period: istanbulDayRange(from, to),
      participants: participants
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0),
    });
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-3">
      <div>
        <label className="label" htmlFor="ygg-from">
          Dönem Başlangıcı
        </label>
        <input
          id="ygg-from"
          type="date"
          className="input"
          required
          value={from}
          max={to}
          onChange={(e) => setFrom(e.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="ygg-to">
          Dönem Bitişi
        </label>
        <input
          id="ygg-to"
          type="date"
          className="input"
          required
          value={to}
          min={from}
          onChange={(e) => setTo(e.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="ygg-participants">
          Katılımcılar (virgülle ayrılmış)
        </label>
        <input
          id="ygg-participants"
          className="input"
          placeholder="ör. Kalite Müdürü, Üretim Müdürü"
          value={participants}
          onChange={(e) => setParticipants(e.target.value)}
        />
      </div>
      {rangeError && (
        <p className="text-xs text-rose-600 dark:text-rose-400 sm:col-span-3">{rangeError}</p>
      )}
      {error && (
        <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900 sm:col-span-3">
          {error}
        </div>
      )}
      <div className="sm:col-span-3">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Oluşturuluyor…' : 'Paket Oluştur'}
        </button>
      </div>
    </form>
  );
}

function YggDetail({
  id,
  canWrite,
  onBack,
}: {
  id: string;
  canWrite: boolean;
  onBack: () => void;
}) {
  const query = useQuery<YggRecord>({
    queryKey: ['ygg', 'detail', id],
    queryFn: () => api.get(`/v1/ygg/${id}`),
  });

  if (query.isLoading) {
    return (
      <div className="card p-6 text-sm text-slate-500 dark:text-slate-400">Yükleniyor…</div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="card p-6 text-sm text-rose-700 dark:text-rose-300">
        {query.error instanceof Error ? query.error.message : 'Kayıt bulunamadı'}
        <div className="mt-3">
          <button type="button" className="btn-secondary" onClick={onBack}>
            ← Listeye Dön
          </button>
        </div>
      </div>
    );
  }

  return <YggDetailBody key={query.data._id} record={query.data} canWrite={canWrite} onBack={onBack} />;
}

function YggDetailBody({
  record,
  canWrite,
  onBack,
}: {
  record: YggRecord;
  canWrite: boolean;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const finalized = record.status === 'FINALIZED';
  const editable = canWrite && !finalized;

  const [meetingDate, setMeetingDate] = useState(
    record.meetingDate ? record.meetingDate.slice(0, 10) : '',
  );
  const [participants, setParticipants] = useState(record.participants.join(', '));
  const [manualInputs, setManualInputs] = useState<ManualInputs>(record.manualInputs);
  const [decisions, setDecisions] = useState<YggDecision[]>(record.decisions);
  const [previousActions, setPreviousActions] = useState<YggPrevAction[]>(record.previousActions);
  const [newDecisionText, setNewDecisionText] = useState('');
  const [newActionText, setNewActionText] = useState<Record<string, string>>({});
  const [newActionOwner, setNewActionOwner] = useState<Record<string, string>>({});
  const [newActionDue, setNewActionDue] = useState<Record<string, string>>({});

  const capaList = useQuery({
    queryKey: ['capa', 'list', 'for-ygg'],
    queryFn: () => api.get<{ items: { _id: string; capaNumber: string; title: string }[]; total: number }>('/v1/capa?limit=200'),
  });
  const capaOptions = capaList.data?.items ?? [];

  function applyServerRecord(updated: YggRecord) {
    qc.setQueryData(['ygg', 'detail', record._id], updated);
    void qc.invalidateQueries({ queryKey: ['ygg', 'list'] });
  }

  const save = useMutation({
    mutationFn: () =>
      api.patch<YggRecord>(`/v1/ygg/${record._id}`, {
        meetingDate: meetingDate ? new Date(meetingDate + 'T00:00:00Z').toISOString() : null,
        participants: participants
          .split(',')
          .map((p) => p.trim())
          .filter((p) => p.length > 0),
        manualInputs,
        decisions,
        previousActions,
      }),
    onSuccess: applyServerRecord,
  });

  const finalize = useMutation({
    mutationFn: () => api.post<YggRecord>(`/v1/ygg/${record._id}/finalize`),
    onSuccess: applyServerRecord,
  });

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function downloadPdf() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await api.get<Blob>(`/v1/ygg/${record._id}/report?format=pdf`, { raw: true });
      downloadBlob(blob, `ygg-${record.yggNumber}.pdf`);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'İndirme başarısız');
    } finally {
      setDownloading(false);
    }
  }

  function handleFinalize() {
    if (window.confirm('YGG kaydı kesinleştirilecek ve değiştirilemez olacak. Emin misiniz?')) {
      finalize.mutate();
    }
  }

  function addDecision() {
    const text = newDecisionText.trim();
    if (!text) return;
    setDecisions((prev) => [...prev, { id: newLocalId(), text, actions: [] }]);
    setNewDecisionText('');
  }

  function addAction(decisionId: string) {
    const description = (newActionText[decisionId] ?? '').trim();
    if (!description) return;
    const owner = (newActionOwner[decisionId] ?? '').trim();
    const due = newActionDue[decisionId] ?? '';
    setDecisions((prev) =>
      prev.map((d) =>
        d.id === decisionId
          ? {
              ...d,
              actions: [
                ...d.actions,
                {
                  id: newLocalId(),
                  description,
                  owner,
                  dueDate: due ? new Date(due + 'T00:00:00Z').toISOString() : null,
                  status: 'OPEN' as const,
                  capaId: null,
                  capaNumber: null,
                },
              ],
            }
          : d,
      ),
    );
    setNewActionText((prev) => ({ ...prev, [decisionId]: '' }));
    setNewActionOwner((prev) => ({ ...prev, [decisionId]: '' }));
    setNewActionDue((prev) => ({ ...prev, [decisionId]: '' }));
  }

  function setActionCapa(decisionId: string, actionId: string, capa: CapaRef) {
    setDecisions((prev) =>
      prev.map((d) =>
        d.id === decisionId
          ? { ...d, actions: d.actions.map((a) => (a.id === actionId ? { ...a, ...capa } : a)) }
          : d,
      ),
    );
  }

  function togglePrevAction(index: number) {
    setPreviousActions((prev) =>
      prev.map((a, i) =>
        i === index ? { ...a, status: a.status === 'OPEN' ? ('DONE' as const) : ('OPEN' as const) } : a,
      ),
    );
  }

  const totalActionCount = decisions.reduce((s, d) => s + d.actions.length, 0);
  const mutationError = save.error ?? finalize.error;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <button type="button" className="btn-secondary text-xs" onClick={onBack}>
          ← Listeye Dön
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={downloading}
            onClick={downloadPdf}
          >
            {downloading ? 'PDF üretiliyor…' : 'PDF İndir'}
          </button>
        </div>
      </div>

      <PageHeader
        title={`YGG ${record.yggNumber}`}
        subtitle={`Dönem: ${istanbulDisplayDate(record.period.from)} – ${istanbulDisplayDate(record.period.to)}`}
        clause="§9.3"
        actions={
          <span className={`badge ${STATUS_BADGE[record.status].class}`}>
            {STATUS_BADGE[record.status].label}
          </span>
        }
      />

      {downloadError && (
        <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900">
          {downloadError}
        </div>
      )}

      {/* Otomatik girdi özet kartları — record.autoInputs'tan, salt-okunur snapshot */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard title="KPI (SE-PR-26)">
          <Kv label="Uygun Ürün" value={fmtPct(record.autoInputs.kpi.goodPartsPercent)} />
          <Kv label="OTD" value={fmtPct(record.autoInputs.kpi.onTimeDeliveryPercent)} />
          <Kv label="Hurda Oranı" value={fmtPct(record.autoInputs.kpi.scrapRatePercent)} />
          <Kv label="Puanlanan Sevk Satırı" value={record.autoInputs.kpi.shipmentLines.scored} />
          <Kv label="Müşteri Memnuniyeti" value={fmtPct(record.autoInputs.kpi.customerSatisfactionPercent)} />
        </SummaryCard>
        <SummaryCard title="NCR (§8.7)">
          <Kv label="Toplam NCR" value={record.autoInputs.ncr.totalNcrs} />
          <Kv label="Hurda" value={record.autoInputs.ncr.totalScrapQty} />
          <Kv label="Yeniden İşlem" value={record.autoInputs.ncr.totalReworkQty} />
          <Kv
            label="İlk Pareto"
            value={
              record.autoInputs.ncr.topPareto[0]
                ? `${record.autoInputs.ncr.topPareto[0].label} (${record.autoInputs.ncr.topPareto[0].count})`
                : '—'
            }
          />
        </SummaryCard>
        <SummaryCard title="CAPA (§10.2)">
          <Kv label="Açık" value={record.autoInputs.capa.open} />
          <Kv label="Geciken" value={record.autoInputs.capa.overdue} />
          <Kv label="Dönemde Kapanan" value={record.autoInputs.capa.closedInPeriod} />
        </SummaryCard>
        <SummaryCard title="Tetkik (§9.2)">
          <Kv label="Majör" value={record.autoInputs.audit.findingsByCategory.MAJOR} />
          <Kv label="Minör" value={record.autoInputs.audit.findingsByCategory.MINOR} />
          <Kv label="Gözlem" value={record.autoInputs.audit.findingsByCategory.OBSERVATION} />
          <Kv label="OFI" value={record.autoInputs.audit.findingsByCategory.OFI} />
        </SummaryCard>
      </div>

      {/* Toplantı bilgisi */}
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
          Toplantı Bilgisi
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="ygg-meeting-date">
              Toplantı Tarihi
            </label>
            <input
              id="ygg-meeting-date"
              type="date"
              className="input"
              value={meetingDate}
              disabled={!editable}
              onChange={(e) => setMeetingDate(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="ygg-detail-participants">
              Katılımcılar (virgülle ayrılmış)
            </label>
            <input
              id="ygg-detail-participants"
              className="input"
              value={participants}
              disabled={!editable}
              onChange={(e) => setParticipants(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Önceki aksiyonların takibi — status toggle yalnız DRAFT + canWrite */}
      {previousActions.length > 0 && (
        <div className="card overflow-x-auto p-5">
          <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
            Önceki Aksiyonların Takibi
          </h2>
          <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
            <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-2 py-2 text-left">YGG No</th>
                <th className="px-2 py-2 text-left">Açıklama</th>
                <th className="px-2 py-2 text-left">Sorumlu</th>
                <th className="px-2 py-2 text-left">Termin</th>
                <th className="px-2 py-2 text-left">Durum</th>
                <th className="px-2 py-2 text-left">CAPA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {previousActions.map((a, i) => (
                <tr key={i}>
                  <td className="whitespace-nowrap px-2 py-2 font-mono text-xs text-slate-700 dark:text-slate-300">
                    {a.yggNumber}
                  </td>
                  <td className="px-2 py-2 text-slate-800 dark:text-slate-200">{a.description}</td>
                  <td className="px-2 py-2 text-xs text-slate-500 dark:text-slate-400">
                    {a.owner || '—'}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-500 dark:text-slate-400">
                    {a.dueDate ? new Date(a.dueDate).toLocaleDateString('tr-TR') : '—'}
                  </td>
                  <td className="px-2 py-2">
                    {editable ? (
                      <button
                        type="button"
                        className="btn-secondary text-xs"
                        onClick={() => togglePrevAction(i)}
                      >
                        {a.status === 'OPEN' ? 'AÇIK' : 'TAMAMLANDI'}
                      </button>
                    ) : (
                      <span className="badge bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {a.status === 'OPEN' ? 'AÇIK' : 'TAMAMLANDI'}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                    {a.capaNumber ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 6 manuel bölüm */}
      <div className="space-y-4">
        {MANUAL_SECTION_KEYS.map((key) => (
          <ManualSectionCard
            key={key}
            sectionKey={key}
            section={manualInputs[key]}
            editable={editable}
            onChange={(next) => setManualInputs((prev) => ({ ...prev, [key]: next }))}
          />
        ))}
      </div>

      {/* Kararlar ve aksiyonlar */}
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
          Kararlar ve Aksiyonlar ({totalActionCount} aksiyon)
        </h2>
        <div className="space-y-4">
          {decisions.map((d) => (
            <DecisionCard
              key={d.id}
              decision={d}
              editable={editable}
              newActionText={newActionText[d.id] ?? ''}
              newActionOwner={newActionOwner[d.id] ?? ''}
              newActionDue={newActionDue[d.id] ?? ''}
              onActionTextChange={(v) => setNewActionText((prev) => ({ ...prev, [d.id]: v }))}
              onActionOwnerChange={(v) => setNewActionOwner((prev) => ({ ...prev, [d.id]: v }))}
              onActionDueChange={(v) => setNewActionDue((prev) => ({ ...prev, [d.id]: v }))}
              onAddAction={() => addAction(d.id)}
              capaOptions={capaOptions}
              onActionCapaChange={(actionId, capa) => setActionCapa(d.id, actionId, capa)}
            />
          ))}
          {decisions.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">Henüz karar eklenmedi.</p>
          )}
        </div>

        <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
          <label className="label" htmlFor="ygg-new-decision">
            Yeni Karar
          </label>
          <div className="flex items-center gap-2">
            <input
              id="ygg-new-decision"
              className="input"
              placeholder="Karar metni"
              value={newDecisionText}
              disabled={!editable}
              onChange={(e) => setNewDecisionText(e.target.value)}
            />
            <button
              type="button"
              className="btn-secondary shrink-0"
              disabled={!editable}
              onClick={addDecision}
            >
              Karar Ekle
            </button>
          </div>
        </div>
      </div>

      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={finalize.isPending}
            onClick={handleFinalize}
          >
            {finalize.isPending ? 'Kesinleştiriliyor…' : 'Kesinleştir'}
          </button>
          {mutationError && (
            <span className="text-xs text-rose-600">
              {mutationError instanceof Error ? mutationError.message : 'İşlem başarısız'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const fmtPct = (v: number | null): string => (v === null ? '—' : `%${v.toFixed(1)}`);

function SummaryCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="card p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </div>
      <dl className="mt-2 space-y-1">{children}</dl>
    </div>
  );
}

function Kv({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="font-medium text-slate-900 dark:text-slate-100">{value}</dd>
    </div>
  );
}

function ManualSectionCard({
  sectionKey,
  section,
  editable,
  onChange,
}: {
  sectionKey: YggManualSectionKey;
  section: ManualSection;
  editable: boolean;
  onChange: (next: ManualSection) => void;
}) {
  const [guideOpen, setGuideOpen] = useState(false);
  const guide = useQuery<GuideResponse>({
    queryKey: ['ygg-guide', sectionKey],
    queryFn: () => api.get(`/v1/ygg/guide?section=${sectionKey}`),
    enabled: false,
  });

  async function toggleGuide() {
    if (guideOpen) {
      setGuideOpen(false);
      return;
    }
    if (!guide.data) {
      await guide.refetch();
    }
    setGuideOpen(true);
  }

  const textareaId = `ygg-manual-${sectionKey}`;

  return (
    <div className="card p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {MANUAL_SECTION_LABELS[sectionKey]}
        </h3>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
            <input
              type="checkbox"
              checked={section.evaluated}
              disabled={!editable}
              onChange={(e) => onChange({ ...section, evaluated: e.target.checked })}
              className="h-4 w-4"
            />
            Değerlendirildi
          </label>
          <button type="button" className="btn-secondary text-xs" onClick={() => void toggleGuide()}>
            {guide.isFetching ? 'Yükleniyor…' : 'Rehber'}
          </button>
        </div>
      </div>
      <label className="label" htmlFor={textareaId}>
        Değerlendirme Notu
      </label>
      <textarea
        id={textareaId}
        className="input min-h-[88px]"
        disabled={!editable}
        value={section.text}
        onChange={(e) => onChange({ ...section, text: e.target.value })}
      />
      {guideOpen && guide.data && (
        <div className="mt-3 rounded-md bg-slate-50 p-3 text-xs dark:bg-slate-900/50">
          <div className="mb-2 font-medium text-slate-700 dark:text-slate-300">Rehber Sorular</div>
          <ul className="list-inside list-disc space-y-1 text-slate-600 dark:text-slate-400">
            {guide.data.questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
          {guide.data.ragAvailable && guide.data.passages.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="font-medium text-slate-700 dark:text-slate-300">İlgili Pasajlar</div>
              {guide.data.passages.map((p, i) => (
                <div
                  key={i}
                  className="rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900"
                >
                  <div className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
                    {p.docCode} · skor {p.score.toFixed(3)}
                  </div>
                  <div className="mt-1 text-slate-700 dark:text-slate-300">{p.excerpt}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {guideOpen && guide.isError && (
        <div className="mt-3 text-xs text-rose-600">Rehber yüklenemedi.</div>
      )}
    </div>
  );
}

function DecisionCard({
  decision,
  editable,
  newActionText,
  newActionOwner,
  newActionDue,
  onActionTextChange,
  onActionOwnerChange,
  onActionDueChange,
  onAddAction,
  capaOptions,
  onActionCapaChange,
}: {
  decision: YggDecision;
  editable: boolean;
  newActionText: string;
  newActionOwner: string;
  newActionDue: string;
  onActionTextChange: (v: string) => void;
  onActionOwnerChange: (v: string) => void;
  onActionDueChange: (v: string) => void;
  onAddAction: () => void;
  capaOptions: { _id: string; capaNumber: string; title: string }[];
  onActionCapaChange: (actionId: string, capa: CapaRef) => void;
}) {
  const actionInputId = `ygg-new-action-${decision.id}`;
  const ownerInputId = `ygg-new-action-owner-${decision.id}`;
  const dueInputId = `ygg-new-action-due-${decision.id}`;

  return (
    <div className="rounded-md border border-slate-200 p-4 dark:border-slate-800">
      <div className="font-medium text-slate-800 dark:text-slate-200">{decision.text}</div>
      {decision.actions.length > 0 && (
        <table className="mt-2 min-w-full text-xs">
          <thead className="text-slate-500 dark:text-slate-400">
            <tr>
              <th className="py-1 pr-2 text-left">Aksiyon</th>
              <th className="py-1 pr-2 text-left">Sorumlu</th>
              <th className="py-1 pr-2 text-left">Termin</th>
              <th className="py-1 pr-2 text-left">Durum</th>
              <th className="py-1 pr-2 text-left">CAPA</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {decision.actions.map((a) => (
              <tr key={a.id}>
                <td className="py-1 pr-2 text-slate-800 dark:text-slate-200">{a.description}</td>
                <td className="py-1 pr-2 text-slate-500 dark:text-slate-400">{a.owner || '—'}</td>
                <td className="py-1 pr-2 text-slate-500 dark:text-slate-400">
                  {a.dueDate ? new Date(a.dueDate).toLocaleDateString('tr-TR') : '—'}
                </td>
                <td className="py-1 pr-2">{a.status === 'OPEN' ? 'AÇIK' : 'TAMAMLANDI'}</td>
                <td className="py-1 pr-2">
                  {editable ? (
                    <select
                      className="input py-0.5 text-xs"
                      value={a.capaId ?? ''}
                      onChange={(e) => {
                        const picked = capaOptions.find((c) => c._id === e.target.value);
                        onActionCapaChange(a.id, {
                          capaId: picked?._id ?? null,
                          capaNumber: picked?.capaNumber ?? null,
                        });
                      }}
                    >
                      <option value="">— CAPA yok —</option>
                      {capaOptions.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.capaNumber} — {c.title}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                      {a.capaNumber ?? '—'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
        <label className="sr-only" htmlFor={actionInputId}>
          Aksiyon açıklaması
        </label>
        <input
          id={actionInputId}
          className="input flex-1"
          placeholder="Aksiyon açıklaması"
          value={newActionText}
          disabled={!editable}
          onChange={(e) => onActionTextChange(e.target.value)}
        />
        <label className="sr-only" htmlFor={ownerInputId}>
          Sorumlu
        </label>
        <input
          id={ownerInputId}
          className="input w-36"
          placeholder="Sorumlu (opsiyonel)"
          value={newActionOwner}
          disabled={!editable}
          onChange={(e) => onActionOwnerChange(e.target.value)}
        />
        <label className="sr-only" htmlFor={dueInputId}>
          Termin
        </label>
        <input
          id={dueInputId}
          type="date"
          className="input w-40"
          value={newActionDue}
          disabled={!editable}
          onChange={(e) => onActionDueChange(e.target.value)}
        />
        <button
          type="button"
          className="btn-secondary shrink-0"
          disabled={!editable}
          onClick={onAddAction}
        >
          Aksiyon Ekle
        </button>
      </div>
    </div>
  );
}
